import { useEffect, useRef } from 'react'

import { mergeRegister } from '@lexical/utils'
import { useMemoizedFn } from 'ahooks'
import { $getSelection, COMMAND_PRIORITY_EDITOR } from 'lexical'

import { useBlockType } from '../../hooks/useBlockType'
import { BlockType } from '../../hooks/useBlockType/interface'
import { useEditor } from '../../hooks/useEditor'
import { useSelectionStyle } from '../../hooks/useSelectionStyle'
import { useSelectionTextFormat } from '../../hooks/useSelectionTextFormat'

import {
  CANCEL_FORMAT_PAINT_COMMAND,
  DONE_FORMAT_PAINT_COMMAND,
  START_FORMAT_PAINT_COMMAND
} from './commands'
import { cursorSvg } from './cursor'

export const FormatPainterPlugin = () => {
  const [blockType, setBlockType] = useBlockType()
  const [bold, toogleBold] = useSelectionTextFormat('bold')
  const [code, toogleCode] = useSelectionTextFormat('code')
  const [highlight, toogleHighlight] = useSelectionTextFormat('highlight')
  const [italic, toogleItalic] = useSelectionTextFormat('italic')
  const [strikethrough, toogleStrikethrough] =
    useSelectionTextFormat('strikethrough')
  const [subscript, toogleSubscript] = useSelectionTextFormat('subscript')
  const [superscript, toogleSuperscript] = useSelectionTextFormat('superscript')
  const [underline, toogleUnderline] = useSelectionTextFormat('underline')
  const [fontFormily, setFontFormily] = useSelectionStyle({
    name: 'font-formily'
  })
  const [fontSize, setFontSize] = useSelectionStyle({
    name: 'font-size'
  })
  const [fontColor, setFontColor] = useSelectionStyle({
    name: 'color'
  })
  const [backgroundColor, setBackgroundColor] = useSelectionStyle({
    name: 'background-color'
  })

  const editor = useEditor()
  const styleRef = useRef<{
    blockType?: BlockType
    bold?: boolean
    code?: boolean
    highlight?: boolean
    italic?: boolean
    strikethrough?: boolean
    subscript?: boolean
    superscript?: boolean
    underline?: boolean
    fontFormily?: string
    fontSize?: string
    fontColor?: string
    backgroundColor?: string
  }>({})
  const needPaintRef = useRef(false)
  const startPaintListener = useMemoizedFn(() => {
    styleRef.current = {
      blockType,
      bold,
      code,
      highlight,
      italic,
      strikethrough,
      subscript,
      superscript,
      underline,
      fontFormily,
      fontSize,
      fontColor,
      backgroundColor
    }
    needPaintRef.current = true
    return false
  })
  const cancelPaintListener = () => {
    needPaintRef.current = false
    return false
  }
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        START_FORMAT_PAINT_COMMAND,
        startPaintListener,
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        CANCEL_FORMAT_PAINT_COMMAND,
        cancelPaintListener,
        COMMAND_PRIORITY_EDITOR
      )
    )
  }, [editor, startPaintListener])

  const cursorRef = useRef<string>(null)
  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement) return
    if (needPaintRef.current) {
      const currentCursor = window
        .getComputedStyle(rootElement)
        .getPropertyValue('cursor')
      cursorRef.current = currentCursor
      rootElement.style.cursor = `url('data:image/svg+xml;utf8,${encodeURIComponent(cursorSvg)}') 5 10,text`
    } else if (cursorRef.current) {
      rootElement.style.cursor = cursorRef.current
    }
  }, [editor])

  const readyPaintRef = useRef(false)
  const readyPaintHandler = useMemoizedFn(() => {
    readyPaintRef.current = needPaintRef.current
  })
  const doPaintHandler = useMemoizedFn(() => {
    if (!readyPaintRef.current) return
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!selection) {
        return
      }
      if (blockType !== styleRef.current.blockType) {
        setBlockType({ type: blockType, resetIfSame: false })
      }
      if (bold !== styleRef.current.bold) {
        toogleBold()
      }
      if (code !== styleRef.current.code) {
        toogleCode()
      }
      if (highlight !== styleRef.current.highlight) {
        toogleHighlight()
      }
      if (italic !== styleRef.current.italic) {
        toogleItalic()
      }
      if (strikethrough !== styleRef.current.strikethrough) {
        toogleStrikethrough()
      }
      if (subscript !== styleRef.current.subscript) {
        toogleSubscript()
      }
      if (superscript !== styleRef.current.superscript) {
        toogleSuperscript()
      }
      if (underline !== styleRef.current.underline) {
        toogleUnderline()
      }
      if (
        fontFormily !== styleRef.current.fontFormily &&
        styleRef.current.fontFormily
      ) {
        setFontFormily(styleRef.current.fontFormily)
      }
      if (fontSize !== styleRef.current.fontSize && styleRef.current.fontSize) {
        setFontSize(styleRef.current.fontSize)
      }
      if (
        fontColor !== styleRef.current.fontColor &&
        styleRef.current.fontColor
      ) {
        setFontColor(styleRef.current.fontColor)
      }
      if (
        backgroundColor !== styleRef.current.backgroundColor &&
        styleRef.current.backgroundColor
      ) {
        setBackgroundColor(styleRef.current.backgroundColor)
      }
      editor.dispatchCommand(DONE_FORMAT_PAINT_COMMAND, undefined)
    })
  })
  useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (rootElement) {
        rootElement.addEventListener('mousedown', readyPaintHandler)
        rootElement.addEventListener('mouseup', doPaintHandler)
      }
      if (prevRootElement) {
        prevRootElement.removeEventListener('mousedown', readyPaintHandler)
        prevRootElement.removeEventListener('mouseup', doPaintHandler)
      }
    })
  }, [editor, doPaintHandler, readyPaintHandler])

  return null
}
